import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GRAY_DARK, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {Title} from 'ui-library/Headers';
import {Column, Table} from 'ui-library/simple-table/SimpleTable';

import {BenchlingAppActivitySessionsQuery} from './__generated/BenchlingAppActivitySessionsQuery';
import BenchlingAppSessionActivityMessage, {
  BenchlingAppSessionMessageTimestamp,
} from './BenchlingAppSessionActivityMessage';

type BenchlingAppSessionNode = ExtractByTypename<
  BenchlingAppActivitySessionsQuery['benchlingApp'],
  'BenchlingAppNode'
>['sessionListing']['sessions'][number];

type BenchlingAppMessageListingNode = BenchlingAppSessionNode['allMessagesListing'];

export interface SessionActivityMessagesProps {
  session: BenchlingAppSessionNode;
}

function cardRow(content: string | JSX.Element): JSX.Element {
  return (
    <tr className={classNames('messageRow', css(styles.sessionMessageRow))}>
      <td key="col1" />
      <td key="col2" colSpan={3}>
        <div className={classNames('messagesCard', css(styles.messagesCard))}>{content}</div>
      </td>
    </tr>
  );
}

export default function BenchlingAppSessionActivityMessagesTable(
  props: SessionActivityMessagesProps
): JSX.Element {
  const session: BenchlingAppSessionNode = props.session;
  const messagesListing = session.allMessagesListing;
  const noMessagesText = 'No messages recorded for this session.';
  const noMessages = <Title className={css(styles.title)}>{noMessagesText}</Title>;
  return cardRow(messagesListing.total === 0 ? noMessages : messagesTable(messagesListing));
}

function messagesTable(messagesListing: BenchlingAppMessageListingNode): JSX.Element {
  const messages = messagesListing.messages;
  const isTruncated = messages.length < messagesListing.total;
  return (
    <>
      {isTruncated ? (
        <div className={css(styles.limitMessage)}>{`${messages.length} message limit applied`}</div>
      ) : null}
      <Title className={css(styles.title)}>Messages</Title>
      <Table
        numRows={messages.length}
        isCompact={false}
        getRowClassName={() => classNames('simpleTable-row--noShadow', css(styles.messageRow))}
        className={css(styles.messagesTable)}
      >
        <Column
          renderCell={(i) => <BenchlingAppSessionMessageTimestamp timestamp={messages[i].timestamp} />}
        />
        <Column renderCell={(i) => <BenchlingAppSessionActivityMessage message={messages[i]} />} />
      </Table>
    </>
  );
}

export const styles = StyleSheet.create({
  sessionMessageRow: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  title: {
    fontSize: '12px',
  },
  limitMessage: {
    color: GRAY_DARK,
    float: 'right',
  },
  messagesCard: {
    backgroundColor: GRAY_LIGHTER,
    marginRight: '14px',
    marginBottom: '14px',
    padding: '12px',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
  },
  messagesTable: {
    tableLayout: 'auto',
    border: 'none',
  },
  messageRow: {
    height: '36px',
  },
});
